import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, Calendar, ExternalLink, MapPin, AlertTriangle, Share2, ChevronDown, ChevronUp, GraduationCap, Home, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SUPPORTED_STATES = ['Andhra Pradesh', 'Telangana', 'Karnataka'];

// ────────────────────────────────────────────────────────────────────────────
// State picker (compact pill row)
// ────────────────────────────────────────────────────────────────────────────
function StatePicker({ state, setState, testid }) {
  return (
    <div className="inline-flex bg-gray-100 rounded-lg p-1 text-xs" data-testid={testid}>
      {SUPPORTED_STATES.map((s) => (
        <button
          key={s}
          onClick={() => setState(s)}
          className={`px-3 py-1.5 rounded-md font-medium transition ${
            state === s ? 'bg-white shadow text-emerald-700' : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid={`${testid}-${s.replace(/\s+/g, '-').toLowerCase()}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// NDSAP disclaimer (small footer)
// ────────────────────────────────────────────────────────────────────────────
function NdsapDisclaimer({ source }) {
  return (
    <p className="mt-3 text-[11px] text-gray-500 leading-relaxed">
      <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1 text-amber-500" />
      Data sourced from official Indian government portals under NDSAP 2012.
      KnowMySchemes is an independent platform — not an official government website.
      For authoritative records refer to: <em>{source}</em>.
    </p>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 1. MANDI PRICES PANEL  (Kisan Bazaar)
// ────────────────────────────────────────────────────────────────────────────
export function MandiPricesPanel({ defaultState }) {
  const [state, setState] = useState(defaultState || 'Telangana');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/govt-data/mandi-prices`, { params: { state, limit: 12 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], count: 0, source: 'AGMARKNET' }))
      .finally(() => setLoading(false));
  }, [state]);

  const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

  const buildShareText = () => {
    if (!data?.rows?.length) return '';
    const top = data.rows.slice(0, 5);
    return `🌾 Today's Mandi Prices — ${state}\n\n` +
      top.map((r) => `• ${r.crop} (${r.mandi}): ${fmt(r.price_modal)}/qtl ${r.delta_pct >= 0 ? '▲' : '▼'}${Math.abs(r.delta_pct).toFixed(1)}%`).join('\n') +
      `\n\nSource: AGMARKNET (govt). Track live on KnowMySchemes:\nhttps://knowmyschemes.com/money-hub/kisan-bazaar`;
  };

  const handleShare = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try { await navigator.share({ title: `Mandi Prices — ${state}`, text }); }
      catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        alert('Mandi prices copied — paste into WhatsApp.');
      } catch { /* clipboard blocked */ }
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-5 mb-6" data-testid="mandi-prices-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <span>🌾</span> Today's Mandi Prices
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Compare today's modal price across top mandis in {state}.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-md"
            data-testid="mandi-share-button"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>

      <StatePicker state={state} setState={setState} testid="mandi-state-picker" />

      {loading && <p className="mt-4 text-sm text-gray-500">Loading mandi prices…</p>}

      {data && !loading && (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="mandi-prices-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="text-left py-2 font-medium">Crop</th>
                  <th className="text-left py-2 font-medium">Mandi</th>
                  <th className="text-right py-2 font-medium">Modal (₹/quintal)</th>
                  <th className="text-right py-2 font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const up = r.delta_pct > 0;
                  const flat = r.delta_pct === 0;
                  return (
                    <tr key={`${r.crop}-${r.mandi}`} className="border-b border-gray-50 last:border-0">
                      <td className="py-2 pr-2">
                        <div className="font-medium text-gray-900">{r.crop}</div>
                        <div className="text-[10px] text-gray-400">{r.district}</div>
                      </td>
                      <td className="py-2 text-xs text-gray-700">{r.mandi}</td>
                      <td className="py-2 text-right tabular-nums font-medium text-gray-900">
                        {fmt(r.price_modal)}<span className="text-[10px] text-gray-400 ml-0.5 font-normal">/qtl</span>
                      </td>
                      <td className="py-2 text-right">
                        <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${flat ? 'text-gray-500' : up ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {flat ? <Minus className="w-3 h-3" /> : up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {Math.abs(r.delta_pct).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
            <span>
              <Calendar className="w-3 h-3 inline -mt-0.5 mr-1" /> Refreshed {data.last_refreshed} · <strong>1 quintal = 100 kg</strong>
            </span>
            <a href="https://agmarknet.gov.in/" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-700 inline-flex items-center gap-0.5">
              Full data on AGMARKNET <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <NdsapDisclaimer source="AGMARKNET — agmarknet.gov.in" />
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 2. TENDERS PANEL  (Business Hub)
// ────────────────────────────────────────────────────────────────────────────
export function TendersPanel({ defaultState }) {
  const [state, setState] = useState(defaultState || 'Karnataka');
  const [msmeOnly, setMsmeOnly] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/govt-data/tenders`, { params: { state, msme_only: msmeOnly, limit: 10 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [state, msmeOnly]);

  return (
    <div className="bg-white rounded-2xl border border-violet-200 shadow-sm p-5 mb-6" data-testid="tenders-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <span>📋</span> Active Government Tenders
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Public-sector opportunities in {state} — bid via CPPP.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer" data-testid="tenders-msme-toggle">
          <input type="checkbox" checked={msmeOnly} onChange={(e) => setMsmeOnly(e.target.checked)} className="accent-violet-600" />
          MSME-eligible only
        </label>
      </div>

      <StatePicker state={state} setState={setState} testid="tenders-state-picker" />

      {loading && <p className="mt-4 text-sm text-gray-500">Loading tenders…</p>}

      {data && !loading && (
        <>
          {data.rows.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">No tenders match. Try toggling MSME filter.</p>
          ) : (
            <div className="mt-4 space-y-3" data-testid="tenders-list">
              {data.rows.map((t, i) => (
                <div key={`${t.title}-${i}`} className="border border-gray-200 rounded-xl p-4 hover:border-violet-200 transition">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 leading-snug">{t.title}</h4>
                      <p className="text-[11px] text-gray-500 mt-0.5">{t.department} · {t.district}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-sm font-bold text-violet-700">₹{t.value_cr} Cr</span>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${t.days_left <= 7 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                        {t.days_left}d left
                      </span>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                      {t.category}
                    </span>
                    {t.msme_eligible && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold">
                        MSME eligible
                      </span>
                    )}
                  </div>

                  <p className="mt-2 text-xs text-gray-700 leading-relaxed">{t.summary}</p>

                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <a
                      href={t.related_scheme?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-medium text-emerald-700 hover:underline inline-flex items-center gap-1"
                    >
                      Related scheme: <strong>{t.related_scheme?.name}</strong> <ExternalLink className="w-3 h-3" />
                    </a>
                    <a
                      href={t.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 px-3 py-1.5 rounded-md inline-flex items-center gap-1"
                      data-testid={`tender-cta-${i}`}
                    >
                      Apply on CPPP <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          <NdsapDisclaimer source="Central Public Procurement Portal (CPPP) — cppp.gov.in" />
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 3. DISTRICT EDUCATION PANEL  (Skills & Jobs)
// ────────────────────────────────────────────────────────────────────────────

const SCHOOL_TYPE_BADGE = {
  JNV:     { label: 'Navodaya',         className: 'bg-amber-100 text-amber-800' },
  KV:      { label: 'Kendriya Vidyalaya', className: 'bg-sky-100 text-sky-800' },
  Sainik:  { label: 'Sainik',           className: 'bg-rose-100 text-rose-800' },
  Welfare: { label: 'Welfare Residential', className: 'bg-emerald-100 text-emerald-800' },
  Model:   { label: 'Model School',     className: 'bg-indigo-100 text-indigo-800' },
  KGBV:    { label: 'KGBV (Girls)',     className: 'bg-pink-100 text-pink-800' },
  PU:      { label: 'Govt PU College',  className: 'bg-violet-100 text-violet-800' },
};

const SCHOOL_TYPE_FILTERS = [
  { key: 'all',     label: 'All types' },
  { key: 'JNV',     label: 'Navodaya' },
  { key: 'KV',      label: 'Kendriya Vidyalaya' },
  { key: 'Sainik',  label: 'Sainik' },
  { key: 'Welfare', label: 'Welfare Residential' },
  { key: 'Model',   label: 'Model School' },
  { key: 'KGBV',    label: 'KGBV (Girls)' },
];

function DistrictCard({ row, num, schoolTypeFilter, state }) {
  const [open, setOpen] = useState(false);
  const filteredSchools = useMemo(() => {
    if (!row.notable_schools) return [];
    if (schoolTypeFilter === 'all') return row.notable_schools;
    return row.notable_schools.filter((s) => s.type === schoolTypeFilter);
  }, [row.notable_schools, schoolTypeFilter]);

  // Build a "verify on UDISE+" search URL — opens the public dashboard with district pre-selected.
  const verifyUrl = `https://dashboard.udiseplus.gov.in/`;

  return (
    <div className="border border-gray-200 rounded-xl p-4 hover:border-indigo-200 transition" data-testid={`district-card-${row.district}`}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-indigo-600" />
          {row.district}
        </h4>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${row.success_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
          {row.success_rate}% scholarship success
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-[10px] uppercase text-gray-400">Govt schools</p>
          <p className="text-sm font-semibold text-gray-900">{num(row.govt_schools)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-gray-400">Pass rate</p>
          <p className="text-sm font-semibold text-gray-900">{row.avg_pass_rate}%</p>
        </div>
        <div>
          <p className="text-[10px] uppercase text-gray-400">Sanctioned</p>
          <p className="text-sm font-semibold text-gray-900">{num(row.scholarships_sanctioned)}</p>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-indigo-700 bg-indigo-50 px-2 py-1.5 rounded">
        Top scheme here: <strong>{row.top_scheme}</strong>
      </p>

      {row.notable_schools?.length > 0 && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            className="mt-3 w-full text-xs font-medium text-indigo-700 inline-flex items-center justify-between gap-1.5 py-1.5 hover:text-indigo-900"
            data-testid={`district-toggle-${row.district}`}
          >
            <span className="inline-flex items-center gap-1.5">
              <GraduationCap className="w-3.5 h-3.5" />
              {open ? 'Hide' : 'Show'} notable govt schools
              <span className="text-gray-400 font-normal">({filteredSchools.length})</span>
            </span>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {open && (
            <div className="mt-2 space-y-2" data-testid={`district-schools-${row.district}`}>
              {filteredSchools.length === 0 ? (
                <p className="text-xs text-gray-400 italic px-1 py-2">No schools of this type listed for {row.district}. Try "All types".</p>
              ) : filteredSchools.map((s) => {
                const badge = SCHOOL_TYPE_BADGE[s.type] || { label: s.type, className: 'bg-gray-100 text-gray-700' };
                return (
                  <div key={s.name} className="border border-gray-100 rounded-lg p-2.5 bg-white">
                    <div className="flex items-start justify-between gap-2">
                      <h5 className="text-xs font-semibold text-gray-900 leading-snug flex-1">{s.name}</h5>
                      <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${badge.className} shrink-0`}>
                        {badge.label}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[10px] text-gray-500">
                      <span className="inline-flex items-center gap-0.5"><Building2 className="w-3 h-3" /> {s.board}</span>
                      <span>·</span>
                      <span>Classes {s.classes}</span>
                      {s.residential && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-0.5 text-emerald-700 font-medium"><Home className="w-3 h-3" /> Residential</span>
                        </>
                      )}
                    </div>
                    {s.address && <p className="mt-1 text-[10px] text-gray-400">{s.address}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <a
        href={verifyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 text-[10px] text-gray-500 hover:text-indigo-700 inline-flex items-center gap-1"
        data-testid={`district-verify-${row.district}`}
      >
        Verify {row.district} on UDISE+ <ExternalLink className="w-2.5 h-2.5" />
      </a>
    </div>
  );
}

export function EducationPanel({ defaultState }) {
  const [state, setState] = useState(defaultState || 'Andhra Pradesh');
  const [schoolTypeFilter, setSchoolTypeFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/govt-data/district-education`, { params: { state } })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [state]);

  const num = (n) => Number(n).toLocaleString('en-IN');

  const totalSchoolsListed = useMemo(() => {
    if (!data?.rows) return 0;
    return data.rows.reduce((acc, r) => acc + (r.notable_schools?.length || 0), 0);
  }, [data]);

  return (
    <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-5 mb-6" data-testid="education-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <span>🎓</span> District Education Intelligence
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Schools, enrollment & scholarship success rates per district. Tap each district for {totalSchoolsListed} notable government schools.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <StatePicker state={state} setState={setState} testid="education-state-picker" />
        <div className="inline-flex items-center gap-1 flex-wrap">
          <span className="text-[10px] uppercase text-gray-400 mr-1">School type:</span>
          {SCHOOL_TYPE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setSchoolTypeFilter(f.key)}
              className={`text-[10px] font-medium px-2 py-1 rounded-full transition ${
                schoolTypeFilter === f.key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`school-type-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="mt-4 text-sm text-gray-500">Loading…</p>}

      {data && !loading && (
        <>
          {/* State-level totals from UDISE+ 2023-24 */}
          {data.state_total && (
            <div className="mt-4 bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 rounded-xl p-4" data-testid="state-total-banner">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">
                    {state} · UDISE+ 2023-24 totals
                  </p>
                  <div className="mt-1.5 flex items-baseline gap-4 flex-wrap">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">{num(data.state_total.govt_schools)}</span>
                      <span className="text-xs text-gray-500 ml-1">govt schools</span>
                    </div>
                    <div>
                      <span className="text-lg font-semibold text-gray-700">{num(data.state_total.total_schools)}</span>
                      <span className="text-xs text-gray-500 ml-1">total schools (incl. private)</span>
                    </div>
                    <div>
                      <span className="text-lg font-semibold text-gray-700">{(data.state_total.students_enrolled / 1_00_000).toFixed(1)} L</span>
                      <span className="text-xs text-gray-500 ml-1">students enrolled</span>
                    </div>
                  </div>
                </div>
                <a
                  href={data.verify_url || 'https://udiseplus.gov.in/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-indigo-700 bg-white hover:bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
                  data-testid="udise-dashboard-link"
                >
                  Open UDISE+ Dashboard <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                Below: top {data.count} districts in {state} with sample notable govt schools. Tap "Verify" on any district to drill into UDISE+ live.
              </p>
            </div>
          )}

          <div className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="education-cards">
            {data.rows.map((r) => (
              <DistrictCard key={r.district} row={r} num={num} schoolTypeFilter={schoolTypeFilter} state={state} />
            ))}
          </div>

          <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
            <strong>School types:</strong> JNV (Navodaya, central, free, residential) · KV (Kendriya Vidyalaya, central, CBSE) · Sainik (NDA prep, residential) · Welfare Residential (state, free, residential for SC/ST/BC) · Model (state, CBSE/SSC) · KGBV (girls-only residential, classes VI–XII).
          </p>

          <NdsapDisclaimer source="UDISE+ + National Scholarship Portal — udiseplus.gov.in & scholarships.gov.in" />
        </>
      )}
    </div>
  );
}
